import { type WallSweepSystem, type WallSweeps } from "building-types/facades/facadeTypes";
import type { FacesWithMaterial } from "building-types/geometry/facesWithMaterial";
import type { DocEntityHandle, RoomEdgeHandle } from "building-types/handles/docEntityHandleTypes";
import type { ExteriorPalette } from "building-types/materials/exteriorPaletteTypes";
import type { InteriorPalette } from "building-types/materials/interiorPaletteTypes";
import type { PaletteMaterialSlot } from "building-types/materials/paletteMaterialSlotTypes";
import type { Room } from "building-types/rooms/roomTypes";
import { RoomType } from "building-types/rooms/roomTypesEnum";
import type { WallCatalogSettings } from "building-types/settings/wallCatalogSettings";
import type { WallSettings } from "building-types/settings/wallSettings";
import { ViewEntityType } from "building-types/views/viewEntityType";
import type { WallPlaneTrimGeometry } from "building-types/walls2/wallPlaneTrimTypes";
import type { WallAssemblyFinishLayer, WallAssemblyLayer } from "building-types/walls/wallAssemblyTypes";
import type { WallDescriptor, WallSection } from "building-types/walls/wallDesciptorTypes";
import { WallCladdingType } from "building-types/walls/wallDesciptorTypes";
import { WallType } from "building-types/walls/wallEnums";
import { WallLayerType } from "building-types/walls/wallEnums";
import type { WallFaces } from "building-types/walls/wallFacesTypes";
import type { WallFinishFaces } from "building-types/walls/wallFinishFaces";
import { WallMaterialType } from "building-types/walls/wallMaterialTypesEnum";
import type { WallSystem } from "building-types/walls/wallSytemTypes";
import type { SolidWallMass, Wall, WallMass, WallPath } from "building-types/walls/wallTypes";
import { WallLOD } from "building-types/walls/wallTypes";
import type { Table } from "common/data/table";
import type { MaterialSrc } from "common/materials/materialSrc";
import { ensureDefined } from "common/utils/assertOfType";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { LRUObjectCache } from "common/utils/lruObjectCache";
import { radianToDegree } from "common/utils/math";
import { levelInchesNew } from "common/utils/nominalNumbers";
import { materialSrcFromPalette } from "doc/materialLookup";
import { faceArrayViewEntityNew, viewEntityDescriptorNew } from "doc/utils/viewEntityTools";
import { facesWithMaterialNew } from "doc/utils/wallMaterialTools";
import type { Face } from "geom/brep";
import { faceOrNullCopy, tagFaces } from "geom/brep";
import { addConvexPlanesToBSPLeaf } from "geom/bsp";
import type { ClippingNode } from "geom/bspClipping";
import { clipFace, clippingNodeNew } from "geom/bspClipping";
import type { NodeBSP, PlaneBSP } from "geom/bspTypes";
import { closestParamLine2, closestParamNormalizedLine2 } from "geom/closestPoint";
import { DIST_EPSILON, ONE_MINUS_DIST_EPSILON, ZERO_EPSILON } from "geom/core";
import { line2Eval, line2Len, line2Tangent } from "geom/line2";
import { planeIsHorizontal, planeMoveAlongNormal } from "geom/plane";
import { planeBSPByPointAndNormal } from "geom/planeBSP";
import { polyLine2AABB2, polyLine2Area } from "geom/polyLine2";
import { uvTranformationNew, type UVTransformation } from "geom/uv";
import { vec2AngleBetweenCWSimple, vec2Perp } from "geom/vec2";
import { isCombinedGlobalInstanceMirror } from "../../generationTools/generateOpeningsTools";
import type { WallDatumAbstractionProvider } from "../datums/datumAbstractionProviders";
import { makeSolidWallFaces } from "../walls/solidWallFaces";
import { getWallPath, getHostedRoomFromPath } from "../walls/wall";
import { wallPathOpposite } from "../walls/wallDescriptor";
import { mapWallFacesToParentId, wallFacesFromWallMass, wallMassSectionFaces } from "../walls/wallFaces";
import { wallLayerWithFallback, wallMassLayers } from "../walls/wallLayers";
import { makeWallSideFinishMasses } from "../walls/wallSide";
import { wallSideNew } from "../walls/wallSideBasics";
import { getWallAssemblyFinishLayers, getWallAssemblyFinishLayers2 } from "./wallAssembly";

const Z_FIGHTING_ADJUST = 0.05;

interface MaterialRect {
  bottom: number;
  top: number;
  start: number;
  end: number;
  material: PaletteMaterialSlot;
  entityId?: string;
  descriptor?: FacesWithMaterial["descriptor"];
  pickHandle?: DocEntityHandle;
  entityHandleForSelectingReportingUnit?: DocEntityHandle;
  hideLayersBehind?: boolean;
  zOffset?: number;
  materialTransformation?: UVTransformation;
}

function tagFacesWithWallParent(faces: Face[], wallId: string) {
  tagFaces(faces, { type: "PARENT", parentType: "WALL", parentId: wallId });
}

function facadeSpanNoteDescriptor(layer: WallAssemblyLayer): {
  descriptor?: FacesWithMaterial["descriptor"];
  entityId?: string;
} {
  if (
    layer.type !== WallMaterialType.EXTERIOR_FINISH ||
    layer.layerSource?.type !== "FACADE_SPAN_STRATUM" ||
    !layer.elevationNoteEnabled ||
    !layer.elevationNote
  ) {
    return {};
  }

  return {
    descriptor: viewEntityDescriptorNew(layer.layerSource.id, ViewEntityType.NONE, {
      enable: true,
      tagOnlyOne: true,
      text: layer.elevationNote,
    }),
    entityId: layer.layerSource.id,
  };
}

export function materialRectNew(
  bottom: number,
  top: number,
  start: number,
  end: number,
  material: PaletteMaterialSlot,
  entityHandleForSelectingReportingUnit?: DocEntityHandle,
  hideLayersBehind?: boolean,
  materialTransformation?: UVTransformation,
  descriptor?: FacesWithMaterial["descriptor"],
  entityId?: string,
  pickHandle?: DocEntityHandle,
): MaterialRect {
  return {
    bottom,
    top,
    start,
    end,
    material,
    entityId,
    descriptor,
    pickHandle,
    entityHandleForSelectingReportingUnit,
    hideLayersBehind,
    materialTransformation,
  };
}

interface MaterialRectEdgeInfo {
  offset: number;
  nX: number;
  nY: number;
  nZ: number;
  sign: number;
  discardFunc?: () => boolean;
}

function materialRectEdgeInfoNew(
  offset: number,
  nX: number,
  nY: number,
  nZ: number,
  sign: number,
  discardFunc?: () => boolean,
): MaterialRectEdgeInfo {
  return {
    offset,
    nX,
    nY,
    nZ,
    sign,
    discardFunc,
  };
}

function wallFinishFacesNew(): WallFinishFaces {
  return {
    interiorWallFaces: {},
    interiorWallCapFaces: {},
    exteriorWallFaces: {},
    exteriorWallCapFaces: {},
    exteriorWallExtrusions: {},
    exteriorWallBreps: {},
    exteriorWallTrimFaces: {},
    coreFinishFaces: [],
  };
}

/**
 * @deprecated - use collectWallFinishFaces2 instead
 */
export function collectWallFinishFaces({
  wallDatumAbstractionProvider,
  wallSystem,
  wallSweeps,
  interiorPalette,
  exteriorPalette,
  wallSettings,
  wallCatalogSettings,
  mirror,
  enableLegacyDepthOffsets = true,
  outlineFaces = false,
  wallPredicate = () => true,
}: {
  wallDatumAbstractionProvider: WallDatumAbstractionProvider;
  wallSystem: WallSystem;
  wallSweeps?: WallSweepSystem;
  wallSettings: WallSettings;
  wallCatalogSettings: WallCatalogSettings;
  interiorPalette: InteriorPalette;
  exteriorPalette: ExteriorPalette;
  mirror: boolean;
  enableLegacyDepthOffsets?: boolean;
  outlineFaces?: boolean;
  wallPredicate?: (wall: Wall) => boolean;
}): WallFinishFaces {
  const wallFinishFaces = wallFinishFacesNew();

  if (!wallSystem) {
    return wallFinishFaces;
  }

  const { wallMaterials } = wallCatalogSettings;
  const { walls: levelWalls } = wallSystem;
  const levelWallSweeps = wallSweeps?.sweeps;

  if (levelWalls) {
    for (const wallId in levelWalls) {
      const wall = levelWalls[wallId];
      if (!wallPredicate(wall)) {
        continue;
      }

      addWallSweepGeometry({
        wallFinishFacesOut: wallFinishFaces,
        wallSweeps: levelWallSweeps?.[wallId],
      });

      let wallFinishMasses: WallMass[];

      /**
       * Structural and Substrate masses are used for cross section faces
       */
      let wallSubstrateMasses: WallMass[] = [];
      let wallStructuralMasses: WallMass[] = [];

      let leftSideFinishMasses: SolidWallMass[] = [];
      let rightSideFinishMasses: SolidWallMass[] = [];

      let wallFaces: WallFaces;

      switch (wall.wallType) {
        case WallType.SOLID: {
          wallFinishMasses = wallMassLayers(wall.mass, WallLayerType.FINISH, WallLOD.L2);
          wallSubstrateMasses = wallMassLayers(wall.mass, WallLayerType.SUBSTRATE, WallLOD.L2);
          wallStructuralMasses = wallMassLayers(wall.mass, WallLayerType.STRUCTURE, WallLOD.L2);
          leftSideFinishMasses = wall.leftSide.finishes;
          rightSideFinishMasses = wall.rightSide.finishes;
          wallFaces = outlineFaces ? wall.wallOutlineFaces : wall.wallFaces;
          break;
        }
        case WallType.SOLID_MASS: {
          wallFinishMasses = wallMassLayers(wall, WallLayerType.FINISH, WallLOD.L2);
          wallSubstrateMasses = wallMassLayers(wall, WallLayerType.SUBSTRATE, WallLOD.L2);
          wallStructuralMasses = wallMassLayers(wall, WallLayerType.STRUCTURE, WallLOD.L2);
          leftSideFinishMasses = makeWallSideFinishMasses({
            wallDatumAbstractionProvider,
            wallMass: wall,
            wallMaterials,
            isLeftSide: true,
          });
          rightSideFinishMasses = makeWallSideFinishMasses({
            wallDatumAbstractionProvider,
            wallMass: wall,
            wallMaterials,
            isLeftSide: false,
          });
          wallFaces = wallFacesFromWallMass(wall, WallLayerType.FINISH, WallLOD.L2);
          break;
        }
        case WallType.VOID: {
          wallFinishMasses = wallMassLayers(wall, WallLayerType.FINISH, WallLOD.L2);
          wallSubstrateMasses = wallMassLayers(wall, WallLayerType.SUBSTRATE, WallLOD.L2);
          wallStructuralMasses = wallMassLayers(wall, WallLayerType.STRUCTURE, WallLOD.L2);
          leftSideFinishMasses = makeWallSideFinishMasses({
            wallDatumAbstractionProvider,
            wallMass: wall,
            wallMaterials,
            isLeftSide: true,
          });
          rightSideFinishMasses = makeWallSideFinishMasses({
            wallDatumAbstractionProvider,
            wallMass: wall,
            wallMaterials,
            isLeftSide: false,
          });
          /**
           * We create these here because the VOID wall interface does not have a structure
           * for storing these yet.
           * @TODO - split VOID wall into VOID and VOID_MASS, the same way we do
           * for solid walls
           */
          const leftSide = wallSideNew({
            finishes: makeWallSideFinishMasses({
              wallDatumAbstractionProvider,
              wallMass: wall,
              wallMaterials,
              isLeftSide: true,
            }),
          });
          const rightSide = wallSideNew({
            finishes: makeWallSideFinishMasses({
              wallDatumAbstractionProvider,
              wallMass: wall,
              wallMaterials,
              isLeftSide: false,
            }),
          });

          ({ wallFaces } = makeSolidWallFaces({ mass: wall, leftSide, rightSide }));
          break;
        }
        default: {
          assertUnreachable(wall);
        }
      }

      /**
       * These faces are extracted using tagging. More to come
       */

      const topFacesByParentId = mapWallFacesToParentId(wallFaces.topFaces);
      const bottomFacesByParentId = mapWallFacesToParentId(wallFaces.bottomFaces);
      const leftHoleFacesByParentId = mapWallFacesToParentId(wallFaces.leftHoleFaces);
      const rightHoleFacesByParentId = mapWallFacesToParentId(wallFaces.rightHoleFaces);
      const startFaceByParentId = mapWallFacesToParentId(wallFaces.startFaces);
      const endFacesByParentId = mapWallFacesToParentId(wallFaces.endFaces);

      for (const wallCoreMass of [...wallSubstrateMasses, ...wallStructuralMasses]) {
        const wallCoreMassFaces = [
          ...(topFacesByParentId[wallCoreMass.id] ?? []),
          ...(bottomFacesByParentId[wallCoreMass.id] ?? []),
        ];
        wallFinishFaces.coreFinishFaces.push(...wallCoreMassFaces);
      }
      for (const wallSideFinishMass of leftSideFinishMasses) {
        addWallSideFinishMassFaces({
          wallFinishFacesOut: wallFinishFaces,
          wallFacesByParentId: {
            top: topFacesByParentId,
            bottom: bottomFacesByParentId,
            leftHole: leftHoleFacesByParentId,
            start: startFaceByParentId,
            end: endFacesByParentId,
          },
          wallSideFinishMass,
          wallId: wall.id,
          isLeftSide: true,
          wallPath: wallSideFinishMass.path,
          // Always right side for side finish masses
          wallCladdingType: wallSideFinishMass.section.rightSide.cladding,
          wallSideLayers: wallSideFinishMass.wallAssembly.rightCladdingLayers,
          mirror,
        });
      }
      for (const wallSideFinishMass of rightSideFinishMasses) {
        addWallSideFinishMassFaces({
          wallFinishFacesOut: wallFinishFaces,
          wallFacesByParentId: {
            top: topFacesByParentId,
            bottom: bottomFacesByParentId,
            rightHole: rightHoleFacesByParentId,
            start: startFaceByParentId,
            end: endFacesByParentId,
          },
          wallSideFinishMass,
          wallId: wall.id,
          isLeftSide: false,
          wallPath: wallSideFinishMass.path,
          // Always right side for side finish masses
          wallCladdingType: wallSideFinishMass.section.rightSide.cladding,
          wallSideLayers: wallSideFinishMass.wallAssembly.rightCladdingLayers,
          mirror,
        });
      }

      /**
       * These faces have no mappings - they are
       * generated by splits with the roof and do not
       * currently conform to the wall assembly of this wall
       */

      addWallSideExteriorFaces({
        wallFinishFacesOut: wallFinishFaces,
        wall,
        wallExteriorFaces: wallFaces.leftExteriorFaces,
        isLeftSide: true,
      });

      addWallSideExteriorFaces({
        wallFinishFacesOut: wallFinishFaces,
        wall,
        wallExteriorFaces: wallFaces.rightExteriorFaces,
        isLeftSide: false,
      });

      /**
       * These faces are extracted using the @deprecated bound selection heuristic.
       * This whole loop should be removed once all faces are applied using tags
       */

      for (let i = 0; i < wallFinishMasses.length; i++) {
        const wallFinishLayer = wallFinishMasses[i];
        if (wallFinishLayer.wallType === WallType.VOID) {
          continue;
        }
        const wallSection = wallFinishLayer.section;
        const wallSectionFaceSelector: WallSection =
          // if this is the top section, we need to include
          // any faces that have been injected by arched/round
          // openings that may be cutting through roof walls.
          i === wallFinishMasses.length - 1
            ? {
                ...wallSection,
                // adjust the top offset of this section in order to select
                // wallSectionFaces above the top of this wall.
                // TODO - roof walls should pass all of their information down
                // to the wall in order to generate this wall section
                topOffset: levelInchesNew(Number.POSITIVE_INFINITY),
              }
            : wallSection;

        const wallFinishLayerFaces = wallMassSectionFaces(wallFinishLayer, wallFaces, wallSectionFaceSelector);

        const faceSideOptions = {
          wallDatumAbstractionProvider,
          wallFinishFacesOut: wallFinishFaces,
          wall,
          wallFinishLayer,
          wallSettings,
          enableLegacyDepthOffsets,
          interiorPalette,
          exteriorPalette,
          mirror,
        };

        addWallSectionSideFinishFaces({
          ...faceSideOptions,
          wallFinishLayerSideFaces: wallFinishLayerFaces.leftFaces,
          isLeftSide: true,
        });

        addWallSectionSideFinishFaces({
          ...faceSideOptions,
          wallFinishLayerSideFaces: wallFinishLayerFaces.rightFaces,
          isLeftSide: false,
        });
      }
    }
  }

  return wallFinishFaces;
}

interface IncludeFaceConditions {
  top?: boolean;
  bottom?: boolean;
  leftHole?: boolean;
  rightHole?: boolean;
  start?: boolean;
  end?: boolean;
  left?: boolean;
  right?: boolean;
}

const defaultIncludeFaceConditions: Required<IncludeFaceConditions> = {
  top: true,
  bottom: true,
  leftHole: true,
  rightHole: true,
  start: true,
  end: true,
  left: true,
  right: true,
};

type WallFaceConditionKey = keyof Required<IncludeFaceConditions>;
type WallFacesByParentId = Partial<Record<WallFaceConditionKey, Record<string, Face[]>>>;

const wallFaceConditionKeys: WallFaceConditionKey[] = Object.keys(
  defaultIncludeFaceConditions,
) as WallFaceConditionKey[];

function includeFaceConditionsHash(includeFaceConditions: Required<IncludeFaceConditions>): string {
  return wallFaceConditionKeys.map((key) => `${key}:${includeFaceConditions[key]}`).join(",");
}

function makeIncludeFaceConditions(getValue: (key: WallFaceConditionKey) => boolean): Required<IncludeFaceConditions> {
  const includeFaceConditions: Required<IncludeFaceConditions> = {
    ...defaultIncludeFaceConditions,
  };

  for (const key of wallFaceConditionKeys) {
    includeFaceConditions[key] = getValue(key);
  }

  return includeFaceConditions;
}

function mergeIncludeFaceConditions(
  a: Required<IncludeFaceConditions>,
  b: Required<IncludeFaceConditions>,
): Required<IncludeFaceConditions> {
  return makeIncludeFaceConditions((key) => a[key] || b[key]);
}

function resolveIncludeFaceConditions(conditions: IncludeFaceConditions): Required<IncludeFaceConditions> {
  return makeIncludeFaceConditions((key) => conditions[key] ?? true);
}

/**
 * The entity a click on a wall-plane-authored finish layer selects: the most specific host in the
 * layer's parent chain, or the layer's own source when it has no chain.
 *
 * The chain only ever holds generated `WALL_PLANE` and `BUILDING_GRAPH_PLANAR_TOPOLOGY` handles,
 * which the allocator always assigns an id to, so the most specific entry is always a valid answer.
 */
export function wallAssemblyLayerPickHandle(layer: WallAssemblyLayer): DocEntityHandle | undefined {
  // parentChain is ordered from least to most specific.
  const parentChain = layer.parentChain;
  if (parentChain && parentChain.length > 0) {
    return parentChain[parentChain.length - 1];
  }
  return layer.layerSource;
}

const wallFinishFacesCache: LRUObjectCache<string, WallFinishFaces> = new LRUObjectCache(100, (a, b) => a === b);

export function collectWallFinishFaces2({
  wallDatumAbstractionProvider,
  wallSystem,
  wallSweeps,
  wallCatalogSettings,
  mirror,
  outlineFaces = false,
  wallPredicate = () => true,
  includeExteriorFaceConditions = {},
  includeInteriorFaceConditions = {},
}: {
  wallDatumAbstractionProvider: WallDatumAbstractionProvider;
  wallSystem: WallSystem;
  wallSweeps?: WallSweepSystem;
  wallCatalogSettings: WallCatalogSettings;
  mirror: boolean;
  outlineFaces?: boolean;
  wallPredicate?: (wall: Wall) => boolean;
  includeExteriorFaceConditions?: IncludeFaceConditions;
  includeInteriorFaceConditions?: IncludeFaceConditions;
}): WallFinishFaces {
  const { walls: levelWalls } = wallSystem;
  const levelWallSweeps = wallSweeps?.sweeps;

  const resolvedIncludeExteriorFaceConditions = resolveIncludeFaceConditions(includeExteriorFaceConditions);
  const resolvedIncludeInteriorFaceConditions = resolveIncludeFaceConditions(includeInteriorFaceConditions);

  const sweepIds = levelWallSweeps ? Object.keys(levelWallSweeps) : [];
  // No per-document-load component: everything below is geometry and handle-bearing view
  // entities, all derived from the keyed inputs, so two loads of the same wall system produce
  // identical faces. Measured before removing the old one - it suppressed valid hits and never
  // separated differing content. Anything id-derived added to WallFinishFaces would break that.
  const cacheKey = [
    wallSystem.id,
    sweepIds.join(","),
    mirror,
    includeFaceConditionsHash(resolvedIncludeExteriorFaceConditions),
    includeFaceConditionsHash(resolvedIncludeInteriorFaceConditions),
  ].join("-");

  if (wallFinishFacesCache.has(cacheKey)) {
    // NOTE(Andrew): Uncomment this in case you think it is not necessary
    // console.log("wallFinishFaces CACHE HIT", cacheKey);
    return ensureDefined(wallFinishFacesCache.get(cacheKey));
  }

  const { top, bottom, leftHole, rightHole, start, end, left, right } = mergeIncludeFaceConditions(
    resolvedIncludeExteriorFaceConditions,
    resolvedIncludeInteriorFaceConditions,
  );

  const { wallMaterials } = wallCatalogSettings;

  const wallFinishFaces = wallFinishFacesNew();

  if (levelWalls) {
    for (const wallId in levelWalls) {
      const wall = levelWalls[wallId];
      if (!wallPredicate(wall)) {
        continue;
      }

      addWallSweepGeometry({
        wallFinishFacesOut: wallFinishFaces,
        wallSweeps: levelWallSweeps?.[wallId],
      });

      /**
       * Structural and Substrate masses are used for cross section faces
       */
      let wallSubstrateMasses: WallMass[] = [];
      let wallStructuralMasses: WallMass[] = [];

      let leftSideFinishMasses: SolidWallMass[] = [];
      let rightSideFinishMasses: SolidWallMass[] = [];

      let wallFaces: WallFaces;

      switch (wall.wallType) {
        case WallType.SOLID: {
          wallSubstrateMasses = wallMassLayers(wall.mass, WallLayerType.SUBSTRATE, WallLOD.L2);
          wallStructuralMasses = wallMassLayers(wall.mass, WallLayerType.STRUCTURE, WallLOD.L2);
          leftSideFinishMasses = wall.leftSide.finishes;
          rightSideFinishMasses = wall.rightSide.finishes;
          wallFaces = outlineFaces ? wall.wallOutlineFaces : wall.wallFaces;
          break;
        }
        case WallType.SOLID_MASS: {
          wallSubstrateMasses = wallMassLayers(wall, WallLayerType.SUBSTRATE, WallLOD.L2);
          wallStructuralMasses = wallMassLayers(wall, WallLayerType.STRUCTURE, WallLOD.L2);
          leftSideFinishMasses = makeWallSideFinishMasses({
            wallDatumAbstractionProvider,
            wallMass: wall,
            wallMaterials,
            isLeftSide: true,
          });
          rightSideFinishMasses = makeWallSideFinishMasses({
            wallDatumAbstractionProvider,
            wallMass: wall,
            wallMaterials,
            isLeftSide: false,
          });
          wallFaces = wallFacesFromWallMass(wall, WallLayerType.FINISH, WallLOD.L2);
          break;
        }
        case WallType.VOID: {
          wallSubstrateMasses = wallMassLayers(wall, WallLayerType.SUBSTRATE, WallLOD.L2);
          wallStructuralMasses = wallMassLayers(wall, WallLayerType.STRUCTURE, WallLOD.L2);
          leftSideFinishMasses = makeWallSideFinishMasses({
            wallDatumAbstractionProvider,
            wallMass: wall,
            wallMaterials,
            isLeftSide: true,
          });
          rightSideFinishMasses = makeWallSideFinishMasses({
            wallDatumAbstractionProvider,
            wallMass: wall,
            wallMaterials,
            isLeftSide: false,
          });
          /**
           * We create these here because the VOID wall interface does not have a structure
           * for storing these yet.
           * @TODO - split VOID wall into VOID and VOID_MASS, the same way we do
           * for solid walls
           */
          const leftSide = wallSideNew({
            finishes: makeWallSideFinishMasses({
              wallDatumAbstractionProvider,
              wallMass: wall,
              wallMaterials,
              isLeftSide: true,
            }),
          });
          const rightSide = wallSideNew({
            finishes: makeWallSideFinishMasses({
              wallDatumAbstractionProvider,
              wallMass: wall,
              wallMaterials,
              isLeftSide: false,
            }),
          });

          ({ wallFaces } = makeSolidWallFaces({ mass: wall, leftSide, rightSide }));
          break;
        }
        default: {
          assertUnreachable(wall);
        }
      }

      /**
       * These faces are extracted using tagging. More to come
       */

      const topFacesByParentId = top ? mapWallFacesToParentId(wallFaces.topFaces) : {};
      const bottomFacesByParentId = bottom ? mapWallFacesToParentId(wallFaces.bottomFaces) : {};
      const leftHoleFacesByParentId = leftHole ? mapWallFacesToParentId(wallFaces.leftHoleFaces) : {};
      const rightHoleFacesByParentId = rightHole ? mapWallFacesToParentId(wallFaces.rightHoleFaces) : {};
      const startFaceByParentId = start ? mapWallFacesToParentId(wallFaces.startFaces) : {};
      const endFacesByParentId = end ? mapWallFacesToParentId(wallFaces.endFaces) : {};
      const leftFacesByParentId = left ? mapWallFacesToParentId(wallFaces.leftFaces) : {};
      const rightFacesByParentId = right ? mapWallFacesToParentId(wallFaces.rightFaces) : {};

      const commonWallFaces: WallFacesByParentId = {
        top: topFacesByParentId,
        bottom: bottomFacesByParentId,
        start: startFaceByParentId,
        end: endFacesByParentId,
      };

      for (const wallCoreMass of [...wallSubstrateMasses, ...wallStructuralMasses]) {
        // We do not split the internals of the core masse faces into different
        // layers, so they are rendered as a single color
        const wallCoreMassFaces = [
          ...(topFacesByParentId[wallCoreMass.id] ?? []),
          ...(bottomFacesByParentId[wallCoreMass.id] ?? []),
          ...(startFaceByParentId[wallCoreMass.id] ?? []),
          ...(endFacesByParentId[wallCoreMass.id] ?? []),
        ];
        wallFinishFaces.coreFinishFaces.push(...wallCoreMassFaces);

        const { path, wallAssembly } = wallCoreMass;
        const { structuralLayers } = wallAssembly;

        const leftCladdingType = wallCoreMass.section.leftSide.cladding;
        addWallSideFinishMassFaces({
          wallFinishFacesOut: wallFinishFaces,
          wallPath: path,
          wallCladdingType: leftCladdingType,
          wallFacesByParentId: {
            left: leftFacesByParentId,
            leftHole: leftHoleFacesByParentId,
          },
          // Outer layer for left side is the first layer in the core
          wallSideLayers: [...structuralLayers].reverse(),
          wallSideFinishMass: wallCoreMass,
          wallId: wall.id,
          isLeftSide: true,
          mirror,
          includeExteriorFaceConditions: resolvedIncludeExteriorFaceConditions,
          includeInteriorFaceConditions: resolvedIncludeInteriorFaceConditions,
        });

        const rightCladdingType = wallCoreMass.section.rightSide.cladding;
        addWallSideFinishMassFaces({
          wallFinishFacesOut: wallFinishFaces,
          wallPath: path,
          wallCladdingType: rightCladdingType,
          wallFacesByParentId: {
            right: rightFacesByParentId,
            rightHole: rightHoleFacesByParentId,
          },
          // Outer layer for right side is the last layer in the core
          wallSideLayers: structuralLayers,
          wallSideFinishMass: wallCoreMass,
          wallId: wall.id,
          isLeftSide: false,
          mirror,
          includeExteriorFaceConditions: resolvedIncludeExteriorFaceConditions,
          includeInteriorFaceConditions: resolvedIncludeInteriorFaceConditions,
        });
      }

      for (const wallSideFinishMass of leftSideFinishMasses) {
        const { section: wallSection, wallAssembly, path } = wallSideFinishMass;
        // Always right side for side Finish Masses
        const wallSideLayers = getWallAssemblyFinishLayers2(wallAssembly, wallSection, false);
        const wallCladdingType = wallSection.rightSide.cladding;
        addWallSideFinishMassFaces({
          wallPath: path,
          wallCladdingType,
          wallFinishFacesOut: wallFinishFaces,
          wallFacesByParentId: {
            left: leftFacesByParentId,
            leftHole: leftHoleFacesByParentId,
            ...commonWallFaces,
          },
          wallSideLayers,
          wallSideFinishMass,
          wallId: wall.id,
          isLeftSide: true,
          mirror,
          includeExteriorFaceConditions: resolvedIncludeExteriorFaceConditions,
          includeInteriorFaceConditions: resolvedIncludeInteriorFaceConditions,
        });
      }
      for (const wallSideFinishMass of rightSideFinishMasses) {
        const { section: wallSection, wallAssembly, path } = wallSideFinishMass;
        // Always right side for side Finish Masses
        const wallSideLayers = getWallAssemblyFinishLayers2(wallAssembly, wallSection, false);
        const wallCladdingType = wallSection.rightSide.cladding;

        addWallSideFinishMassFaces({
          wallFinishFacesOut: wallFinishFaces,
          wallFacesByParentId: {
            right: rightFacesByParentId,
            rightHole: rightHoleFacesByParentId,
            ...commonWallFaces,
          },
          wallPath: path,
          wallCladdingType,
          wallSideLayers,
          wallSideFinishMass,
          wallId: wall.id,
          isLeftSide: false,
          mirror,
          includeExteriorFaceConditions: resolvedIncludeExteriorFaceConditions,
          includeInteriorFaceConditions: resolvedIncludeInteriorFaceConditions,
        });
      }

      /**
       * These faces have no mappings - they are
       * generated by splits with the roof and do not
       * currently conform to the wall assembly of this wall
       */

      addWallSideExteriorFaces({
        wallFinishFacesOut: wallFinishFaces,
        wall,
        wallExteriorFaces: wallFaces.leftExteriorFaces,
        isLeftSide: true,
      });

      addWallSideExteriorFaces({
        wallFinishFacesOut: wallFinishFaces,
        wall,
        wallExteriorFaces: wallFaces.rightExteriorFaces,
        isLeftSide: false,
      });
    }
  }

  wallFinishFacesCache.add(cacheKey, wallFinishFaces);

  return wallFinishFaces;
}

/**
 * Creates facesWithMaterial representing  faces of individual finish layers
 * of a wall at a cutPlane on a single side. these faces are added to the wallFinishFacesOut object that is
 * passed into the function.
 */
function addWallSideFinishMassFaces({
  wallFinishFacesOut,
  wallSideFinishMass,
  wallFacesByParentId,
  wallId,
  isLeftSide,
  mirror = false,
  wallPath,
  wallCladdingType,
  wallSideLayers,
  includeExteriorFaceConditions = resolveIncludeFaceConditions({}),
  includeInteriorFaceConditions = resolveIncludeFaceConditions({}),
}: {
  wallFinishFacesOut: WallFinishFaces;
  wallSideFinishMass: WallMass;
  wallFacesByParentId: WallFacesByParentId;
  wallId: string;
  wallPath: WallPath;
  wallCladdingType: WallCladdingType;
  isLeftSide: boolean;
  mirror?: boolean;
  wallSideLayers: WallAssemblyLayer[];
  includeExteriorFaceConditions?: Required<IncludeFaceConditions>;
  includeInteriorFaceConditions?: Required<IncludeFaceConditions>;
}) {
  const { exteriorWallFaces, exteriorWallCapFaces, interiorWallFaces, interiorWallCapFaces } = wallFinishFacesOut;
  const { leftRoom, rightRoom, wallDescriptor } = wallPath;

  const wallSideRoom = isLeftSide ? leftRoom : rightRoom;
  const oppWallSideRoom = isLeftSide ? rightRoom : leftRoom;

  let wallFaceId: string | undefined;
  let wallSideFacesWithMaterial: { [wallFaceId: string]: FacesWithMaterial[] } | undefined;
  let wallCapFacesWithMaterial: { [wallFaceId: string]: FacesWithMaterial[] } | undefined;
  let includeFaceConditions: Required<IncludeFaceConditions> | undefined;

  switch (wallCladdingType) {
    case WallCladdingType.INTERIOR: {
      wallFaceId = wallSideRoom?.id;
      wallSideFacesWithMaterial = interiorWallFaces;
      wallCapFacesWithMaterial = interiorWallCapFaces;
      includeFaceConditions = includeInteriorFaceConditions;
      break;
    }
    case WallCladdingType.EXTERIOR: {
      wallFaceId = oppWallSideRoom?.parentId;
      wallSideFacesWithMaterial = exteriorWallFaces;
      wallCapFacesWithMaterial = exteriorWallCapFaces;
      includeFaceConditions = includeExteriorFaceConditions;
      break;
    }
    case WallCladdingType.NONE: {
      break;
    }
    default: {
      assertUnreachable(wallCladdingType);
    }
  }

  if (!wallSideFacesWithMaterial || !wallCapFacesWithMaterial || !includeFaceConditions) {
    return;
  }

  /**
   * @todo(Andrew): to get accurate material counts, we should iterate over
   * every layer rather than just the last (outermost) layer
   */
  const layer = wallSideLayers[wallSideLayers.length - 1];
  if (!layer) {
    return;
  }

  let [dirx, diry] = line2Tangent(wallPath.startX, wallPath.startY, wallPath.endX, wallPath.endY);
  if (isLeftSide) {
    dirx = -dirx;
    diry = -diry;
  }
  const hostedRoom = getHostedRoomFromPath(wallSideRoom, oppWallSideRoom);
  const effectiveMirror = isCombinedGlobalInstanceMirror(mirror, hostedRoom);
  const [nx, ny] = effectiveMirror ? vec2Perp(dirx, -diry) : vec2Perp(dirx, diry);
  const rotationRadians = vec2AngleBetweenCWSimple(nx, ny, 0, 1);
  const materialTransformation = uvTranformationNew({
    rotationDegrees: radianToDegree(rotationRadians),
    isMirrored: effectiveMirror,
  });

  const wallSideFacesWithTransform: Face[] = [];
  const wallCapFacesWithTransform: Face[] = [];
  const wallSideFacesWithoutTransform: Face[] = [];
  const wallCapFacesWithoutTransform: Face[] = [];

  for (const key of wallFaceConditionKeys) {
    if (!includeFaceConditions[key]) {
      continue;
    }

    const faces = wallFacesByParentId[key]?.[wallSideFinishMass.id] ?? [];
    for (const face of faces) {
      if (polyLine2Area(face.loops[0]) < ZERO_EPSILON) {
        continue;
      }

      // TODO - move this check into wall face creation
      const useTransform = planeIsHorizontal(face.plane);
      const isSideFace = key === "left" || key === "right";

      if (useTransform && isSideFace) {
        wallSideFacesWithTransform.push(face);
      }

      if (useTransform && !isSideFace) {
        wallCapFacesWithTransform.push(face);
      }

      if (!useTransform && isSideFace) {
        wallSideFacesWithoutTransform.push(face);
      }

      if (!useTransform && !isSideFace) {
        wallCapFacesWithoutTransform.push(face);
      }
    }
  }

  if (wallSideFacesWithMaterial === exteriorWallFaces) {
    tagFacesWithWallParent(wallSideFacesWithTransform, wallId);
    tagFacesWithWallParent(wallCapFacesWithTransform, wallId);
    tagFacesWithWallParent(wallSideFacesWithoutTransform, wallId);
    tagFacesWithWallParent(wallCapFacesWithoutTransform, wallId);
  }

  const parentRoomEdge = wallAssemblyLayerParentRoomEdgeHandle(wallDescriptor, false);
  const entity = parentRoomEdge ?? layer.layerSource;
  const instanceId = wallSideRoom?.instanceId ?? oppWallSideRoom?.instanceId;

  let pickHandle: DocEntityHandle | undefined = undefined;

  if (layer.layerSource?.type === "WALL_PLANE_RULE") {
    // only run this path for layers created by wall planes for now
    pickHandle = wallAssemblyLayerPickHandle(layer);
  } else if (instanceId) {
    // Inside a component instance, a wall side selects the instance, which the room's pick handle already points at.
    pickHandle = wallSideRoom?.pickHandle ?? oppWallSideRoom?.pickHandle;
  } else if (entity) {
    pickHandle = entity;
  }

  let entityHandleForSelectingReportingUnit: DocEntityHandle | undefined;
  if (leftRoom?.instanceId) {
    entityHandleForSelectingReportingUnit = { type: "COMPONENT_INSTANCE", id: leftRoom.instanceId };
  } else if (rightRoom?.instanceId) {
    entityHandleForSelectingReportingUnit = { type: "COMPONENT_INSTANCE", id: rightRoom.instanceId };
  } else if (entity && entity.type !== "EDGE") {
    entityHandleForSelectingReportingUnit = entity;
  } else if (entity?.type === "EDGE" && entity.parent) {
    entityHandleForSelectingReportingUnit = entity.parent;
  }

  const { descriptor, entityId } = facadeSpanNoteDescriptor(layer);
  const material =
    layer.type === WallMaterialType.EXTERIOR_FINISH || layer.type === WallMaterialType.INTERIOR_FINISH
      ? layer.finishMaterial
      : undefined;

  if (!material) {
    return;
  }

  const addFaces = (faces: Face[], transform: UVTransformation | undefined, out: Record<string, FacesWithMaterial[]>) =>
    addWallFacesWithMaterial({
      wallFaceId,
      wallFacesWithMaterial: out,
      faces,
      material,
      pickHandle,
      entityHandleForSelectingReportingUnit,
      materialTransformation: transform,
      descriptor,
      entityId,
    });

  addFaces(wallSideFacesWithTransform, materialTransformation, wallSideFacesWithMaterial);
  addFaces(wallCapFacesWithTransform, materialTransformation, wallCapFacesWithMaterial);
  addFaces(wallSideFacesWithoutTransform, undefined, wallSideFacesWithMaterial);
  addFaces(wallCapFacesWithoutTransform, undefined, wallCapFacesWithMaterial);
}

interface AddWallSideExteriorFacesOptions {
  wallFinishFacesOut: WallFinishFaces;
  wall: Wall;
  wallExteriorFaces: Face[];
  isLeftSide: boolean;
}

function addWallSideExteriorFaces({
  wallFinishFacesOut,
  wall,
  wallExteriorFaces,
  isLeftSide,
}: AddWallSideExteriorFacesOptions) {
  const { exteriorWallFaces } = wallFinishFacesOut;
  const path = getWallPath(wall);
  const { leftRoom, rightRoom, wallDescriptor } = path;
  const { leftSide, rightSide } = wallDescriptor;

  const wallSide = isLeftSide ? leftSide : rightSide;
  const oppWallSideRoom = isLeftSide ? rightRoom : leftRoom;

  if (wallExteriorFaces.length === 0) {
    return;
  }

  tagFacesWithWallParent(wallExteriorFaces, wall.id);

  addWallFacesWithMaterial({
    wallFaceId: oppWallSideRoom?.parentId,
    wallFacesWithMaterial: exteriorWallFaces,
    faces: wallExteriorFaces,
    material: wallSide.wallExteriorMaterial,
  });
}

interface AddWallSectionSideFinishFacesOptions {
  wallDatumAbstractionProvider: WallDatumAbstractionProvider;
  wallFinishFacesOut: WallFinishFaces;
  wall: Wall;
  wallFinishLayer: SolidWallMass;
  wallFinishLayerSideFaces: Face[];
  wallSettings: WallSettings;
  enableLegacyDepthOffsets: boolean;
  isLeftSide: boolean;
  interiorPalette: InteriorPalette;
  exteriorPalette: ExteriorPalette;
  mirror: boolean;
}

/**
 * @deprecated this function is actively being removed and replaced with
 * a tagged face material system
 */
function addWallSectionSideFinishFaces({
  wallDatumAbstractionProvider,
  wallFinishFacesOut,
  wall,
  wallFinishLayer,
  wallFinishLayerSideFaces,
  wallSettings,
  enableLegacyDepthOffsets,
  isLeftSide,
  interiorPalette,
  exteriorPalette,
  mirror,
}: AddWallSectionSideFinishFacesOptions) {
  const { exteriorWallFaces, interiorWallFaces } = wallFinishFacesOut;
  const { path, wallAssembly, section: wallSection } = wallFinishLayer;
  const { leftRoom, rightRoom, wallDescriptor } = path;
  const { leftSide, rightSide } = wallDescriptor;

  const wallSide = isLeftSide ? leftSide : rightSide;
  const wallSideRoom = isLeftSide ? leftRoom : rightRoom;
  const oppWallSideRoom = isLeftSide ? rightRoom : leftRoom;

  const wallSectionSide = isLeftSide ? wallSection.leftSide : wallSection.rightSide;
  const wallFinishLayers = getWallAssemblyFinishLayers(wallAssembly, wallSection, isLeftSide);

  const wallSectionCutPlane = (wallSection.bottomOffset + wallSection.topOffset) / 2;
  const wallSubstrateLayer = wallLayerWithFallback({
    wall,
    wallLayer: WallLayerType.SUBSTRATE,
    wallLOD: WallLOD.L2,
    cutPlane: wallSectionCutPlane,
  });
  if (!wallSubstrateLayer || wallSubstrateLayer.wallType === WallType.VOID) {
    return { startEndFacesAdded: false }; // TODO
  }

  // utility functions for adding faces
  const addWallFacesWithFinishes = ({
    wallFaceId = "Unknown",
    wallFacesWithMaterial,
    faces,
    room,
    finishLayers = [],
    isViewEntity,
  }: {
    wallFaceId?: string;
    wallFacesWithMaterial: { [wallFaceId: string]: FacesWithMaterial[] };
    faces: Face[];
    room?: Room;
    finishLayers?: WallAssemblyFinishLayer[];
    isViewEntity?: boolean;
  }) => {
    if (!faces.length) {
      return;
    }
    if (!wallFacesWithMaterial[wallFaceId]) {
      wallFacesWithMaterial[wallFaceId] = [];
    }
    if (finishLayers.length > 0) {
      addWallFinishFaces({
        wallDatumAbstractionProvider,
        facesWithMaterial: wallFacesWithMaterial[wallFaceId],
        faces,
        wallAssemblyFinishLayers: finishLayers,
        exteriorPalette,
        interiorPalette,
        room,
        wallSubstrateLayer,
        wallFinishLayer,
        wallSettings,
        enableLegacyDepthOffsets,
        isLeftSide,
        mirror,
      });
    } else if (isViewEntity) {
      wallFacesWithMaterial[wallFaceId].push(faceArrayViewEntityNew(faces));
    }
  };

  switch (wallSectionSide.cladding) {
    case WallCladdingType.INTERIOR: {
      const wallFaceId = wallSideRoom?.id;
      const wallFacesWithMaterial = interiorWallFaces;
      const room = wallSideRoom;

      addWallFacesWithFinishes({
        wallFaceId,
        wallFacesWithMaterial,
        faces: wallFinishLayerSideFaces,
        room,
        finishLayers: wallFinishLayers,
        isViewEntity: false,
      });
      break;
    }
    case WallCladdingType.EXTERIOR: {
      const wallFaceId = oppWallSideRoom?.parentId;
      const wallFacesWithMaterial = exteriorWallFaces;
      const room = oppWallSideRoom;

      tagFacesWithWallParent(wallFinishLayerSideFaces, wall.id);

      addWallFacesWithFinishes({
        wallFaceId,
        wallFacesWithMaterial,
        faces: wallFinishLayerSideFaces,
        room,
        finishLayers: wallFinishLayers,
        isViewEntity: true,
      });
      break;
    }
    case WallCladdingType.NONE: {
      if (wallFinishLayerSideFaces.length) {
        // the existence of faces here indicates that they were injected
        // into the SolidWall wallFaces by a downstream generation step,
        // probably from a non-rectilinear opening with filler faces.
        tagFacesWithWallParent(wallFinishLayerSideFaces, wall.id);
        const wallFaceId = oppWallSideRoom?.parentId;
        const wallFacesWithMaterial = exteriorWallFaces;
        addWallFacesWithMaterial({
          wallFaceId,
          wallFacesWithMaterial,
          faces: wallFinishLayerSideFaces,
          material: wallSide.wallExteriorMaterial,
        });
      }
      break;
    }
    default: {
      assertUnreachable(wallSectionSide.cladding);
    }
  }

  return undefined;
}

function addWallFacesWithMaterial({
  wallFaceId = "Unknown",
  wallFacesWithMaterial,
  faces,
  material,
  pickHandle,
  entityHandleForSelectingReportingUnit,
  materialTransformation,
  descriptor,
  entityId,
}: {
  wallFaceId?: string;
  wallFacesWithMaterial: { [wallFaceId: string]: FacesWithMaterial[] };
  faces: Face[];
  material: PaletteMaterialSlot;
  pickHandle?: DocEntityHandle;
  entityHandleForSelectingReportingUnit?: DocEntityHandle;
  materialTransformation?: UVTransformation;
  descriptor?: FacesWithMaterial["descriptor"];
  entityId?: string;
}) {
  if (!faces.length) {
    return;
  }
  if (!wallFacesWithMaterial[wallFaceId]) {
    wallFacesWithMaterial[wallFaceId] = [];
  }
  wallFacesWithMaterial[wallFaceId].push(
    facesWithMaterialNew(
      faces,
      material,
      entityHandleForSelectingReportingUnit,
      materialTransformation,
      descriptor,
      entityId,
      pickHandle,
    ),
  );
}

function addWallSweepGeometry({
  wallFinishFacesOut,
  wallSweeps,
  room,
}: {
  wallFinishFacesOut: WallFinishFaces;
  room?: Room;
  wallSweeps?: WallSweeps;
}) {
  if (!wallSweeps) {
    return;
  }

  const { exteriorWallExtrusions, exteriorWallBreps, exteriorWallTrimFaces } = wallFinishFacesOut;
  const { leftWallSweeps, rightWallSweeps } = wallSweeps;
  const { geometry: leftWallSweepGeometry } = leftWallSweeps;
  const { geometry: rightWallSweepGeometry } = rightWallSweeps;

  const blockId = room?.parentId ?? "Unknown";

  if (leftWallSweepGeometry.materialExtrusions.length > 0) {
    if (!exteriorWallExtrusions[blockId]) {
      exteriorWallExtrusions[blockId] = [];
    }
    exteriorWallExtrusions[blockId].push(...leftWallSweepGeometry.materialExtrusions);
  }

  if (rightWallSweepGeometry.materialExtrusions.length > 0) {
    if (!exteriorWallExtrusions[blockId]) {
      exteriorWallExtrusions[blockId] = [];
    }
    exteriorWallExtrusions[blockId].push(...rightWallSweepGeometry.materialExtrusions);
  }

  if (leftWallSweepGeometry.materialBreps.length > 0) {
    if (!exteriorWallBreps[blockId]) {
      exteriorWallBreps[blockId] = [];
    }
    exteriorWallBreps[blockId].push(...leftWallSweepGeometry.materialBreps);
  }

  if (rightWallSweepGeometry.materialBreps.length > 0) {
    if (!exteriorWallBreps[blockId]) {
      exteriorWallBreps[blockId] = [];
    }
    exteriorWallBreps[blockId].push(...rightWallSweepGeometry.materialBreps);
  }

  if (leftWallSweepGeometry.materialFaces.length > 0) {
    if (!exteriorWallTrimFaces[blockId]) {
      exteriorWallTrimFaces[blockId] = [];
    }
    exteriorWallTrimFaces[blockId].push(...leftWallSweepGeometry.materialFaces);
  }

  if (rightWallSweepGeometry.materialFaces.length > 0) {
    if (!exteriorWallTrimFaces[blockId]) {
      exteriorWallTrimFaces[blockId] = [];
    }
    exteriorWallTrimFaces[blockId].push(...rightWallSweepGeometry.materialFaces);
  }
}

interface AddWallFinishFacesOptions {
  wallDatumAbstractionProvider: WallDatumAbstractionProvider;
  facesWithMaterial: FacesWithMaterial[];
  faces: Face[];
  exteriorPalette: ExteriorPalette;
  interiorPalette: InteriorPalette;
  room?: Room;
  wallSubstrateLayer: SolidWallMass;
  wallFinishLayer: SolidWallMass;
  wallSettings: WallSettings;
  enableLegacyDepthOffsets?: boolean;
  wallAssemblyFinishLayers: WallAssemblyFinishLayer[];
  isLeftSide: boolean;
  mirror?: boolean;
}

/**
 * AddWallFinishFaces exists in a hybrid state of migration to finish thickness faces.
 * This function uses a new positioning method for all non-facade finish faces, and a legacy positioning
 * method for all wall assembly faces.
 *
 * @deprecated this function is actively being removed and replaced with
 * a tagged face material system
 */
export function addWallFinishFaces({
  wallDatumAbstractionProvider,
  facesWithMaterial,
  faces,
  wallAssemblyFinishLayers,
  exteriorPalette,
  interiorPalette,
  room,
  wallSubstrateLayer,
  wallFinishLayer,
  wallSettings,
  enableLegacyDepthOffsets = true,
  isLeftSide,
  mirror = false,
}: AddWallFinishFacesOptions) {
  const srcRefFace = isLeftSide ? wallFinishLayer.leftFace : wallFinishLayer.rightFace;
  const { loops } = srcRefFace;
  const refAABB = polyLine2AABB2(loops[0]);

  const substrateEdge = isLeftSide ? wallSubstrateLayer.leftEdge : wallSubstrateLayer.rightEdge;
  const finishEdge = isLeftSide ? wallFinishLayer.leftEdge : wallFinishLayer.rightEdge;

  const roomType = room?.type ?? RoomType.GENERAL;

  const wallSidePath = isLeftSide ? wallPathOpposite(wallFinishLayer.path) : wallFinishLayer.path;
  const { startX, startY, endX, endY, wallDescriptor } = wallSidePath;
  const pathLen = line2Len(startX, startY, endX, endY);

  /**
   * we know that the finish layers here should apply to all faces
   * vertically since we have already split the wall at the finish locations.
   * Because certain wall faces (like window infills) can be outside the bounds of the wall layer,
   * we need to adjust the lower and upper bounds ot account for those.
   *
   * @TODO - generate wall sections above and below top of wall and bottom of wall,
   * or inject those window infills into roof faces rather than wall faces
   */
  const lowerDatumRelativeToFace = Number.NEGATIVE_INFINITY;
  const upperDatumRelativeToFace = Number.POSITIVE_INFINITY;

  const matRects: MaterialRect[] = [];
  for (const finishLayer of wallAssemblyFinishLayers) {
    /**
     * Horizontal Splits are already handled by the wall assembly. We don't handle vertical
     * splits yet as individual faces, so we need to create those here using the start and end datums of the finish layer
     * relative to the finish face.
     */
    const { startDatumUsage, endDatumUsage } = finishLayer;

    const startDatumAbstraction = wallDatumAbstractionProvider.getWallHorizontalDatumAbstraction(
      startDatumUsage,
      "WALL_START",
    );
    const endDatumAbstraction = wallDatumAbstractionProvider.getWallHorizontalDatumAbstraction(
      endDatumUsage,
      "WALL_END",
    );

    let startDatumRelativeToFace: number;
    let endDatumRelativeToFace: number;

    const wallPathStartT = startDatumAbstraction.resolveHorizontalDatum(pathLen, startDatumUsage.offset);
    const wallPathEndT = endDatumAbstraction.resolveHorizontalDatum(pathLen, endDatumUsage.offset);

    const [sptX, sptY] = line2Eval(startX, startY, endX, endY, wallPathStartT, false);
    const [eptX, eptY] = line2Eval(startX, startY, endX, endY, wallPathEndT, false);

    const substrateStartT = closestParamNormalizedLine2(
      substrateEdge[0],
      substrateEdge[1],
      substrateEdge[2],
      substrateEdge[3],
      sptX,
      sptY,
    );
    const substrateEndT = closestParamNormalizedLine2(
      substrateEdge[0],
      substrateEdge[1],
      substrateEdge[2],
      substrateEdge[3],
      eptX,
      eptY,
    );

    const finishStartT = closestParamLine2(finishEdge[0], finishEdge[1], finishEdge[2], finishEdge[3], sptX, sptY);
    const finishEndT = closestParamLine2(finishEdge[0], finishEdge[1], finishEdge[2], finishEdge[3], eptX, eptY);

    if (substrateStartT <= DIST_EPSILON) {
      // miter start - extend to start of finish face
      startDatumRelativeToFace = refAABB.minX;
    } else {
      startDatumRelativeToFace = finishStartT;
    }
    if (substrateEndT >= ONE_MINUS_DIST_EPSILON) {
      // miter end - extend to end of finish face
      endDatumRelativeToFace = refAABB.maxX;
    } else {
      endDatumRelativeToFace = finishEndT;
    }

    let materialSrc: MaterialSrc | undefined;
    let materialTransformation: UVTransformation | undefined;
    switch (finishLayer.type) {
      case WallMaterialType.EXTERIOR_FINISH: {
        materialSrc = materialSrcFromPalette(exteriorPalette, finishLayer.finishMaterial);
        materialTransformation = finishLayer.materialTransformation
          ? {
              ...finishLayer.materialTransformation,
              isMirrored: isCombinedGlobalInstanceMirror(mirror, room),
            }
          : undefined;
        break;
      }
      case WallMaterialType.INTERIOR_FINISH: {
        materialSrc = materialSrcFromPalette(interiorPalette, finishLayer.finishMaterial, roomType);
        break;
      }
      default: {
        break;
      }
    }

    if (!materialSrc) {
      continue;
    }

    const parentRoomEdge = wallAssemblyLayerParentRoomEdgeHandle(wallDescriptor, false);

    let entityHandleForSelectingReportingUnit: DocEntityHandle | undefined;
    if (room?.instanceId) {
      entityHandleForSelectingReportingUnit = { type: "COMPONENT_INSTANCE", id: room.instanceId };
    } else if (parentRoomEdge) {
      entityHandleForSelectingReportingUnit = parentRoomEdge.parent;
    } else if (finishLayer.layerSource) {
      entityHandleForSelectingReportingUnit = finishLayer.layerSource;
    }

    // A finish layer inside a component instance selects the instance, which is what the room's own
    // pick handle already points at; otherwise it selects the room edge, or the layer's source.
    let pickHandle: DocEntityHandle | undefined = undefined;
    if (room?.instanceId) {
      pickHandle = room.pickHandle;
    } else if (parentRoomEdge) {
      pickHandle = parentRoomEdge;
    } else if (finishLayer.layerSource) {
      pickHandle = finishLayer.layerSource;
    }

    const { descriptor, entityId } = facadeSpanNoteDescriptor(finishLayer);

    const matRect = materialRectNew(
      lowerDatumRelativeToFace,
      upperDatumRelativeToFace,
      startDatumRelativeToFace,
      endDatumRelativeToFace,
      finishLayer.finishMaterial,
      entityHandleForSelectingReportingUnit,
      !materialSrc.keepPreviousLayers,
      materialTransformation,
      descriptor,
      entityId,
      pickHandle,
    );

    matRects.push(matRect);
  }

  // debug matrects
  // matRects.forEach((r, i) => (r.zOffset = i * 30));

  adjustRectsZOffset(matRects, wallSettings, enableLegacyDepthOffsets);

  for (const face of faces) {
    const finalFaces = faceSplitAndAssignRecursive(srcRefFace, face, matRects);

    if (!finalFaces) {
      continue;
    }

    facesWithMaterial.push(...finalFaces);
  }
}

function wallAssemblyLayerParentRoomEdgeHandle(
  wallDescriptor: WallDescriptor,
  isLeftSide: boolean,
): RoomEdgeHandle | null {
  let wallSideParent = isLeftSide ? wallDescriptor.leftSide : wallDescriptor.rightSide;
  if (!wallSideParent.wallRoom.room) {
    wallSideParent = isLeftSide ? wallDescriptor.rightSide : wallDescriptor.leftSide;
  }
  if (wallSideParent.wallRoom.room && wallSideParent.wallRoom.roomEdgeIndex !== undefined) {
    return {
      type: "EDGE",
      shapeId: "outline",
      edgeId: wallSideParent.wallRoom.roomEdgeIndex,
      parent: {
        type: "ROOM",
        id: wallSideParent.wallRoom.room.id,
      },
    };
  }
  return null;
}

function adjustRectsZOffset(matRects: MaterialRect[], wallSettings: WallSettings, enableLegacyDepthOffsets: boolean) {
  for (const rect of matRects) {
    if (!enableLegacyDepthOffsets) {
      rect.zOffset = 0;
      continue;
    }

    rect.zOffset = rect.material === wallSettings.exteriorWallMaterial ? 0 : Z_FIGHTING_ADJUST;
  }
}

const tagSplitAndAssignRecursive = {
  type: "OPERATION",
  operation: "SPLIT_AND_ASSIGN_RECURSIVE",
};

export function faceSplitAndAssignRecursive(
  srcRefFace: Face,
  face: Face | null,
  matRects: MaterialRect[],
  i = 0,
): FacesWithMaterial[] {
  const facesWithMaterial: FacesWithMaterial[] = [];
  if (i >= matRects.length) {
    return facesWithMaterial;
  }

  const occlusionClippingNodes: ClippingNode[] = [];
  for (let j = i + 1, lj = matRects.length; j < lj; j++) {
    const { hideLayersBehind } = matRects[j];

    if (!hideLayersBehind) {
      continue;
    }

    const rectBSP = getClippingBSP(srcRefFace, matRects[j]);

    occlusionClippingNodes.push(clippingNodeNew(rectBSP));
  }

  const {
    material,
    materialTransformation,
    pickHandle,
    entityHandleForSelectingReportingUnit,
    zOffset,
    descriptor,
    entityId,
  } = matRects[i];

  let offsetFace = faceOrNullCopy(face);
  if (offsetFace) {
    offsetFace = adjustFaceForZFighting(offsetFace, zOffset);
  }

  let inFaces: Face[] = offsetFace ? [offsetFace] : [];
  let outFaces: Face[] = [];

  const swapFaces = () => {
    inFaces = outFaces;
    outFaces = [];
  };

  const trimFaces = (trimFunc: (face: Face) => Face[]) => {
    for (const face of inFaces) {
      try {
        outFaces.push(...trimFunc(face));
      } catch (e) {
        const msg = (e as Error).message;
        console.warn(`could not trim out face, ${msg}, reverting back to original face`);
      }
    }

    swapFaces();
  };

  /**
   * I observed multiple cases with a matRect like this:
   *
   * [{
   *   bottom: -Infinity;
   *   top: Infinity;
   *   end: NaN;
   *   start: NaN;
   *   hideLayersBehind: true;
   *   material: "wallExterior0";
   *   materialTransformation: undefined;
   *   zOffset: 0;
   * }]
   *
   * This is a common special case where there is no material variation in the wall.
   * By detecting this case, we can avoid unnecessary clipping which otherwise breaks
   * the wall Face topology into many small Faces which makes downstream systems slower
   * and more complex.
   */
  if (
    matRects.length === 1 &&
    matRects[0].bottom === -Infinity &&
    matRects[0].top === Infinity &&
    isNaN(matRects[0].start) &&
    isNaN(matRects[0].end)
  ) {
    facesWithMaterial.push(
      facesWithMaterialNew(
        tagFaces(inFaces, tagSplitAndAssignRecursive),
        material,
        entityHandleForSelectingReportingUnit,
        materialTransformation,
        descriptor,
        entityId,
        pickHandle,
      ),
    );
    return facesWithMaterial;
  }

  const rectBSP = getClippingBSP(srcRefFace, matRects[i]);

  trimFaces((x) => {
    return clipFace({
      face: x,
      options: {
        clippingNodes: [clippingNodeNew(rectBSP, true)],
      },
    });
  });

  trimFaces((x) => {
    return clipFace({
      face: x,
      options: {
        clippingNodes: occlusionClippingNodes,
      },
    });
  });

  facesWithMaterial.push(
    facesWithMaterialNew(
      tagFaces(inFaces, tagSplitAndAssignRecursive),
      material,
      entityHandleForSelectingReportingUnit,
      materialTransformation,
      descriptor,
      entityId,
      pickHandle,
    ),
  );

  // if success, try the next level
  // Increase the offset only if the material changes.
  const materialSame: boolean = i + 1 < matRects.length && matRects[i + 1].material === material;
  facesWithMaterial.push(...faceSplitAndAssignRecursive(srcRefFace, materialSame ? face : offsetFace, matRects, i + 1));
  return facesWithMaterial;
}

function getClippingBSP(face: Face, materialRect: MaterialRect): NodeBSP | null {
  const planes = getClippingPlanes(face, materialRect);

  return addConvexPlanesToBSPLeaf(planes);
}

function getClippingPlanes(face: Face, materialRect: MaterialRect): PlaneBSP[] {
  const planesBSP: PlaneBSP[] = [];

  const { bottom = 0, top, start, end } = materialRect;
  const { loops, plane } = face;
  const { uX, uY, uZ, vX, vY, vZ, originX, originY, originZ } = plane;
  const { minX, minY, maxX, maxY } = polyLine2AABB2(loops[0]);

  const discardTop = () => {
    return top > maxY;
  };
  const discardBottom = () => {
    return bottom < minY;
  };
  const discardEnd = () => {
    return end > maxX;
  };
  const discardStart = () => {
    return start < minX;
  };

  const edges: MaterialRectEdgeInfo[] = [
    materialRectEdgeInfoNew(top, vX, vY, vZ, 1, discardTop),
    materialRectEdgeInfoNew(bottom, vX, vY, vZ, -1, discardBottom),
    materialRectEdgeInfoNew(end, uX, uY, uZ, 1, discardEnd),
    materialRectEdgeInfoNew(start, uX, uY, uZ, -1, discardStart),
  ];

  for (const edge of edges) {
    const { offset, nX, nY, nZ, sign, discardFunc } = edge;

    if (discardFunc && discardFunc()) {
      continue;
    }

    planesBSP.push(
      planeBSPByPointAndNormal(
        originX + nX * offset,
        originY + nY * offset,
        originZ + nZ * offset,
        nX * sign,
        nY * sign,
        nZ * sign,
      ),
    );
  }

  return planesBSP;
}

export function adjustFacesForZFighting(faces: Face[], offset: number = Z_FIGHTING_ADJUST): Face[] {
  for (const face of faces) {
    adjustFaceForZFighting(face, offset);
  }
  return faces;
}

function adjustFaceForZFighting(face: Face, offset: number = Z_FIGHTING_ADJUST): Face {
  planeMoveAlongNormal(face.plane, offset);
  return face;
}

// TODO: Assign exterior finish block IDs from wall plane trim finish palette instead of a shared "Unknown" bucket.
const WALL_PLANE_TRIM_FINISH_BLOCK_ID = "Unknown";

export function addWallPlaneTrimGeometry(
  wallFinishFacesOut: WallFinishFaces,
  wallPlaneTrims: Table<WallPlaneTrimGeometry>,
): void {
  const { exteriorWallBreps, exteriorWallExtrusions } = wallFinishFacesOut;

  for (const trimId in wallPlaneTrims) {
    const trimGeometry = wallPlaneTrims[trimId];
    if (!trimGeometry) {
      continue;
    }
    const { geometry } = trimGeometry;

    if (geometry.materialBreps.length > 0) {
      if (!exteriorWallBreps[WALL_PLANE_TRIM_FINISH_BLOCK_ID]) {
        exteriorWallBreps[WALL_PLANE_TRIM_FINISH_BLOCK_ID] = [];
      }
      exteriorWallBreps[WALL_PLANE_TRIM_FINISH_BLOCK_ID].push(...geometry.materialBreps);
    }

    if (geometry.materialExtrusions.length > 0) {
      if (!exteriorWallExtrusions[WALL_PLANE_TRIM_FINISH_BLOCK_ID]) {
        exteriorWallExtrusions[WALL_PLANE_TRIM_FINISH_BLOCK_ID] = [];
      }
      exteriorWallExtrusions[WALL_PLANE_TRIM_FINISH_BLOCK_ID].push(...geometry.materialExtrusions);
    }
  }
}
